import { Field } from "@/components/atoms/Field"
import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  Flex,
  Input,
  Portal,
  Stack,
  Text,
} from "@chakra-ui/react"
import { memo, useEffect } from "react"
import { useForm } from "react-hook-form"

const REJECTION_REASON_LABELS: Record<string, string> = {
  transaction_not_found: "Transaction not found",
  amount_mismatch: "Amount mismatch",
  duplicate_submission: "Duplicate submission",
  other: "Other",
}

export interface InvoiceData {
  id: string
  amount_due: number
  amount_paid: number
  verification_status: string | null
  rejection_reason: string | null
  rejection_notes: string | null
}

export interface PaymentConfirmationFormData {
  payment_method: "zelle" | "cash" | "check"
  confirmation_number: string
}

interface PaymentConfirmationModalProps {
  isOpen: boolean
  invoice: InvoiceData | null
  onClose: () => void
  onSubmit: (
    invoiceId: string,
    data: PaymentConfirmationFormData,
  ) => Promise<void>
  isSubmitting?: boolean
}

const PaymentConfirmationModal = memo(
  ({
    isOpen,
    invoice,
    onClose,
    onSubmit,
    isSubmitting = false,
  }: PaymentConfirmationModalProps) => {
    const form = useForm<PaymentConfirmationFormData>({
      defaultValues: {
        payment_method: "zelle",
        confirmation_number: "",
      },
    })

    const paymentMethod = form.watch("payment_method")

    // Calculate outstanding balance
    const outstandingBalance = invoice
      ? invoice.amount_due - invoice.amount_paid
      : 0

    // Format currency
    const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`

    useEffect(() => {
      if (isOpen) {
        form.reset({
          payment_method: "zelle",
          confirmation_number: "",
        })
      }
    }, [isOpen, form])

    const handleSubmit = form.handleSubmit(async (data) => {
      if (!invoice) return
      await onSubmit(invoice.id, data)
      form.reset()
    })

    if (!invoice) return null

    const isRejected = invoice.verification_status === "rejected"

    return (
      <Portal>
        <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
          <DialogBackdrop />
          <DialogPositioner>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirm Payment</DialogTitle>
                <DialogCloseTrigger />
              </DialogHeader>

              <DialogBody>
                {/* Rejection Banner */}
                {isRejected && (
                  <Box
                    p={3}
                    mb={4}
                    bg="red.50"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="red.200"
                  >
                    <Stack gap={2}>
                      <Text fontWeight="bold" color="red.700">
                        Previous Submission Rejected
                      </Text>
                      <Text fontSize="sm" color="red.600">
                        <Text as="span" fontWeight="medium">
                          Reason:{" "}
                        </Text>
                        {invoice.rejection_reason
                          ? REJECTION_REASON_LABELS[invoice.rejection_reason] ||
                            invoice.rejection_reason
                          : "Not specified"}
                      </Text>
                      {invoice.rejection_notes && (
                        <Text fontSize="sm" color="red.600">
                          <Text as="span" fontWeight="medium">
                            Notes:{" "}
                          </Text>
                          {invoice.rejection_notes}
                        </Text>
                      )}
                    </Stack>
                  </Box>
                )}

                {/* Amount Due Box */}
                <Box
                  p={3}
                  mb={4}
                  bg="blue.50"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="blue.200"
                >
                  <Flex justify="space-between" align="center">
                    <Text fontWeight="medium" color="blue.700">
                      Outstanding Balance:
                    </Text>
                    <Text fontWeight="bold" fontSize="lg" color="blue.700">
                      {formatCurrency(outstandingBalance)}
                    </Text>
                  </Flex>
                </Box>

                <form onSubmit={handleSubmit}>
                  <Stack gap={4}>
                    <Field
                      label="Payment Method"
                      required
                      invalid={!!form.formState.errors.payment_method}
                      errorText={form.formState.errors.payment_method?.message}
                    >
                      <select
                        {...form.register("payment_method", {
                          required: "Payment method is required",
                        })}
                        className="chakra-select"
                      >
                        <option value="zelle">Zelle</option>
                        <option value="cash">Cash</option>
                        <option value="check">Check</option>
                      </select>
                    </Field>

                    <Field
                      label="Confirmation Number"
                      required={paymentMethod === "zelle"}
                      invalid={!!form.formState.errors.confirmation_number}
                      errorText={
                        form.formState.errors.confirmation_number?.message
                      }
                      helperText={
                        paymentMethod === "zelle"
                          ? undefined
                          : "Check number or reference (optional)"
                      }
                    >
                      <Input
                        type="text"
                        placeholder={
                          paymentMethod === "zelle"
                            ? "Enter Zelle confirmation number"
                            : "Enter check number or reference"
                        }
                        {...form.register("confirmation_number", {
                          validate: (value) => {
                            if (
                              paymentMethod === "zelle" &&
                              (!value || value.trim() === "")
                            ) {
                              return "Confirmation number is required for Zelle payments"
                            }
                            return true
                          },
                        })}
                      />
                    </Field>
                  </Stack>
                </form>

                {/* Info Box */}
                <Box
                  p={3}
                  mt={4}
                  bg="gray.50"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="gray.200"
                >
                  <Text fontSize="sm" color="gray.600">
                    Our staff will verify your payment within 1-2 business days.
                  </Text>
                </Box>
              </DialogBody>

              <DialogFooter>
                <Button variant="outline" onClick={onClose} mr={3}>
                  Cancel
                </Button>
                <Button
                  colorPalette="blue"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  loadingText="Submitting..."
                >
                  Submit
                </Button>
              </DialogFooter>
            </DialogContent>
          </DialogPositioner>
        </DialogRoot>
      </Portal>
    )
  },
)

PaymentConfirmationModal.displayName = "PaymentConfirmationModal"

export default PaymentConfirmationModal
